import type { Edge, Node } from '../../../core/models/scene';

export interface PositionChartViewport {
  readonly scale: number;
  readonly offsetX: number;
  readonly offsetY: number;
  readonly width: number;
  readonly height: number;
}

/**
 * Keeps the dense Position Chart paint set close to the viewport.
 *
 * One viewport of overscan plus one reporting hop prevents blank edges during
 * compositor panning while avoiding full-tree redraws at normal working zoom.
 */
export function projectPositionChartViewport(
  nodes: readonly Node[],
  edges: readonly Edge[],
  viewport: PositionChartViewport,
): { nodes: Node[]; edges: Edge[] } {
  const scale = Number.isFinite(viewport.scale) && viewport.scale > 0
    ? viewport.scale
    : 1;
  const viewportWidth = Math.max(0, viewport.width / scale);
  const viewportHeight = Math.max(0, viewport.height / scale);
  const left = -viewport.offsetX / scale - viewportWidth;
  const top = -viewport.offsetY / scale - viewportHeight;
  const right = left + viewportWidth * 3;
  const bottom = top + viewportHeight * 3;

  const visibleIds = new Set<string>();
  for (const node of nodes) {
    const width = node.w ?? node.width ?? 270;
    const height = node.h ?? node.height ?? 136;
    if (
      node.x + width >= left
      && node.x <= right
      && node.y + height >= top
      && node.y <= bottom
    ) {
      visibleIds.add(node.id);
    }
  }

  // Preserve connectors at the overscan boundary and make the next pan step
  // immediately paintable without mounting the entire organization.
  const initiallyVisibleIds = new Set(visibleIds);
  for (const edge of edges) {
    if (initiallyVisibleIds.has(edge.from) || initiallyVisibleIds.has(edge.to)) {
      visibleIds.add(edge.from);
      visibleIds.add(edge.to);
    }
  }

  return {
    nodes: nodes.filter((node) => visibleIds.has(node.id)),
    edges: edges.filter((edge) => visibleIds.has(edge.from) && visibleIds.has(edge.to)),
  };
}
